import { selectStandardProps } from "@/utils/componentUtils";
import { Form, Select } from "antd";
import { FormItemProps } from "antd/lib/form";
import { SelectProps } from "antd/lib/select";
import groupBy from "lodash/groupBy";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_LOCALE } from "../../../../app/i18n";
import { useEnumeration } from "../../../../common/api/queries";
import type { UUID } from "../../../../typings/global";
import { CommissionsSettingsLevelType } from "../../../commissions/level/enums";
import { CommissionsSettingsLevelBase } from "../../../commissions/level/types";
import { formatCommissionsLevelName } from "../../../commissions/level/utils";
import { EnumerationType } from "../../enums";
import { FilterFunction } from "../../types";

interface Props {
  formItemProps: FormItemProps;
  selectProps?: SelectProps<string>;
  optionsProps?: CommissionsLevelOptionsProps;
}

interface CommissionsLevelOptionsProps {
  renderOnlyLevelCodeTag?: boolean;
  filterType?: CommissionsSettingsLevelType;
  filter?: FilterFunction<CommissionsSettingsLevelBase>;
}

const CommissionsLevelSelect = ({ formItemProps, selectProps, optionsProps }: Props) => {
  const { t } = useTranslation();

  const commissionsLevelsEnums = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_LEVELS);

  const commissionsLevelToCodeMap = useMemo(
    () => new Map<UUID, string>(commissionsLevelsEnums.map((level) => [level.id, level.code])),
    [commissionsLevelsEnums],
  );

  const resolveCommissionsLevelSelectOptions = useCallback(() => {
    let options = [...commissionsLevelsEnums];

    if (optionsProps?.filterType) {
      options = options.filter((level) => level.type === optionsProps.filterType);
    }

    if (optionsProps?.filter) {
      options = options.filter(optionsProps.filter);
    }

    return options;
  }, [commissionsLevelsEnums, optionsProps]);

  const allOptions = resolveCommissionsLevelSelectOptions();

  if (allOptions.some((item) => item.category)) {
    const optionGroupsWithCategory = groupBy(
      allOptions.filter((level) => level.category),
      (level) => level.category,
    );

    const optionsWithoutCategory = allOptions.filter((level) => !level.category);

    return (
      <Form.Item {...formItemProps}>
        <Select
          {...selectStandardProps}
          {...selectProps}
          labelRender={(props) =>
            optionsProps?.renderOnlyLevelCodeTag ? commissionsLevelToCodeMap.get(props.value.toString()) : props.label
          }
          options={[
            ...Object.keys(optionGroupsWithCategory)
              .sort((a, b) => a.localeCompare(b, DEFAULT_LOCALE, { sensitivity: "accent" }))
              .map((group) => ({
                label: group,
                options:
                  optionGroupsWithCategory[group]?.map((option) => ({
                    value: option.id,
                    label: formatCommissionsLevelName(option),
                  })) ?? [],
              })),
            ...(optionsWithoutCategory.length > 0
              ? [
                  {
                    label: t("common.other"),
                    options: optionsWithoutCategory.map((option) => ({
                      value: option.id,
                      label: formatCommissionsLevelName(option),
                    })),
                  },
                ]
              : []),
          ]}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item {...formItemProps}>
      <Select
        {...selectStandardProps}
        {...selectProps}
        options={allOptions.map((option) => ({
          value: option.id,
          label: formatCommissionsLevelName(option),
        }))}
        labelRender={(props) =>
          optionsProps?.renderOnlyLevelCodeTag ? commissionsLevelToCodeMap.get(props.value.toString()) : props.label
        }
      />
    </Form.Item>
  );
};

export default CommissionsLevelSelect;
